/**
 * Stripe Payment Element の責務集約コンポーネント
 *
 * Stripe に関する知識（loadStripe / Elements / PaymentElement / options / Appearance）を
 * この1ファイルに閉じ込める。React Hook Form の Controller のように「Stripe の責務・知識を
 * 1箇所へ集約する」のが設計意図。
 *
 * presentations 層に Context Provider である Elements を置くのは、この集約のための意図的な例外。
 */
import { STRIPE_PUBLISHABLE_KEY } from '@fukuri-admins/constants/common';
import { Button, Stack } from '@fukuri-admins/ui/common';
import { Elements, ElementsConsumer, PaymentElement } from '@stripe/react-stripe-js';
import type {
  Appearance,
  Stripe,
  StripeElements,
  StripeElementsOptions,
  StripePaymentElementOptions,
} from '@stripe/stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import styles from './styles.module.scss';

// loadStripe はレンダーごとに呼ぶと iframe が再生成されるため、モジュールスコープで1回だけ呼ぶ
const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);

// Appearance（Figma のデザイントークンに寄せる）
const APPEARANCE: Appearance = {
  theme: 'stripe',
  variables: {
    fontFamily: "'Hiragino Sans', sans-serif",
    fontSizeBase: '14px',
    colorText: '#1d1d20',
    colorDanger: '#c70036',
    colorBackground: '#FAFAFA',
    spacingUnit: '8px',
  },
  rules: {
    '.Label': {
      fontSize: '14px',
      color: '#1d1d20',
    },
    '.Input': {
      backgroundColor: 'white',
      border: '1px solid #d0d0d4',
      borderRadius: '8px',
      fontSize: '14px',
      color: '#1d1d20',
      padding: '8px 12px',
      lineHeight: '24px', // 40px の高さを padding 8px 2つ + lineHeight 24px で実現
    },
    '.Input:focus': {
      borderColor: undefined, // デフォルトの focus の borderColor は #0084D1 だが、Figma のデザインでは borderColor は変化しないため undefined にする
      boxShadow: '0 0 0 3px #0084D1',
    },
  },
};

// deferred モード: clientSecret なしで描画できる。SetupIntent は編集ボタン押下時に発行し、
// confirmSetup 実行時に clientSecret として渡す（Elements には渡さない）
const ELEMENTS_OPTIONS: StripeElementsOptions = {
  mode: 'setup',
  currency: 'jpy',
  paymentMethodTypes: ['card'],
  locale: 'ja',
  appearance: APPEARANCE,
};

const PAYMENT_ELEMENT_OPTIONS: StripePaymentElementOptions = {
  layout: {
    type: 'tabs',
  },
  // 国・郵便番号は収集しない（Figma に存在しないため）。
  // ここで 'never' にしたフィールドは confirmSetup 時に自前で渡す義務がある。義務は「Stripe が
  // 実際に入力欄を描画したか」ではなく 'never' の指定有無で判定されるため、描画されない
  // 郵便番号も渡す必要がある（渡さないと IntegrationError になる。実機で確認済み）。
  // 渡す値は constants の BILLING_ADDRESS に定義している。
  fields: {
    billingDetails: {
      address: {
        country: 'never',
        postalCode: 'never',
      },
    },
  },
  // 規約文言は Figma にないため非表示
  terms: {
    card: 'never',
  },
  // Apple Pay / Google Pay / Link はデフォルト 'auto' で勝手に表示されるため明示的に無効化（Figma はプレーンなカードフォームのみ）
  wallets: {
    applePay: 'never',
    googlePay: 'never',
    link: 'never',
  },
};

type Props = {
  /** 保存ボタンを非活性にするか（client_secret 未取得 / SetupIntent 発行中 / 送信中） */
  isSubmitDisabled: boolean;
  /**
   * カード登録に失敗したときのエラー文言。エラーがない場合は undefined。
   *
   * SetupIntent 発行の失敗・カード登録（submit / confirmSetup）の失敗は同時に起こらないため、
   * 表示スロットは保存ボタン付近の1箇所に統合している。
   */
  errorMessage?: string;
  /**
   * 保存ボタン押下時のハンドラ
   *
   * confirmSetup の実行は副作用のため Component Hook の責務。この Presentation は
   * ElementsConsumer から受け取った stripe / elements を引き渡すだけに留める。
   *
   * 渡し先（Button の onClick）とシグネチャが異なるため、`React.ComponentProps` では
   * 型を導出できず手動定義している。
   */
  onSubmit: (stripe: Stripe, elements: StripeElements) => void;
};

/**
 * Stripe Payment Element（カード入力フォーム） + 保存ボタン
 *
 * stripe / elements の取得には `ElementsConsumer`（render props）を使う。
 * `useStripe` / `useElements`（hooks 版）は presentations 層で React hook を使えないため採用しない。
 * React Hook Form の `Controller` と同じ「render props で外部ライブラリの値を受け取る」形になる。
 */
export const StripePaymentField: React.FC<Props> = ({
  isSubmitDisabled,
  errorMessage,
  onSubmit,
}) => (
  <Elements stripe={stripePromise} options={ELEMENTS_OPTIONS}>
    <ElementsConsumer>
      {({ stripe, elements }) => (
        <Stack direction='column' gap={24} className={styles.container}>
          <PaymentElement options={PAYMENT_ELEMENT_OPTIONS} />
          {errorMessage && <p className={styles.errorMessage}>{errorMessage}</p>}
          {/* PaymentElement は送信ボタンを持たないため自前で配置する */}
          <Button
            variant='primary'
            size='sm'
            // stripe / elements は Stripe.js の読み込み完了まで null
            disabled={!stripe || !elements || isSubmitDisabled}
            onClick={() => {
              if (!stripe || !elements) return;
              onSubmit(stripe, elements);
            }}
          >
            保存
          </Button>
        </Stack>
      )}
    </ElementsConsumer>
  </Elements>
);
